import cn from 'classnames';
import { useEffect, useRef, useState } from 'react';

import { buildLeadAudioWave } from '@app/components/LeadCard/ui/MessageHistory/lib';

import type { FC, PointerEvent as ReactPointerEvent } from 'react';

import './LeadAudioWave.scss';

// Ширина столбика фиксированная: чем шире дорожка, тем БОЛЬШЕ столбиков, а не
// толще. Их количество считается по измеренной ширине.
const BAR_WIDTH = 2;
const BAR_GAP = 2;
const MIN_BARS = 8;
const FALLBACK_BARS = 34;

interface LeadAudioWaveProps {
  durationSec: number;
  progress: number;
  isIncoming: boolean;
  onSeek(ratio: number): void;
}

export const LeadAudioWave: FC<LeadAudioWaveProps> = ({
  durationSec,
  progress,
  isIncoming,
  onSeek,
}) => {
  const [trackNode, setTrackNode] = useState<HTMLDivElement | null>(null);
  const [barCount, setBarCount] = useState(FALLBACK_BARS);
  const isDraggingRef = useRef(false);

  useEffect(() => {
    if (!trackNode || typeof ResizeObserver === 'undefined') return undefined;

    const updateBarCount = () => {
      const width = trackNode.clientWidth;
      if (!width) return;

      setBarCount(
        Math.max(
          MIN_BARS,
          Math.floor((width + BAR_GAP) / (BAR_WIDTH + BAR_GAP))
        )
      );
    };

    updateBarCount();

    const observer = new ResizeObserver(updateBarCount);
    observer.observe(trackNode);

    return () => observer.disconnect();
  }, [trackNode]);

  const seekByPointer = (clientX: number) => {
    if (!trackNode) return;

    const rect = trackNode.getBoundingClientRect();
    if (!rect.width) return;

    onSeek(Math.max(0, Math.min(1, (clientX - rect.left) / rect.width)));
  };

  const pointerDownHandler = (event: ReactPointerEvent<HTMLDivElement>) => {
    event.currentTarget.setPointerCapture(event.pointerId);
    isDraggingRef.current = true;
    seekByPointer(event.clientX);
  };

  const pointerMoveHandler = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (isDraggingRef.current) seekByPointer(event.clientX);
  };

  const pointerUpHandler = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }

    isDraggingRef.current = false;
  };

  return (
    <div
      ref={setTrackNode}
      className={cn('LeadAudioWave', {
        'LeadAudioWave--out': !isIncoming,
      })}
      onPointerDown={pointerDownHandler}
      onPointerMove={pointerMoveHandler}
      onPointerUp={pointerUpHandler}
      onPointerCancel={pointerUpHandler}
    >
      {buildLeadAudioWave(durationSec, barCount).map((height, index) => (
        <span
          key={index}
          className={cn('LeadAudioWave__bar', {
            'LeadAudioWave__bar--played': index / barCount < progress,
          })}
          style={{ height: `${Math.round(height * 100)}%` }}
        />
      ))}
    </div>
  );
};
